/**
 * ab-product-card.js — Product card cart UI + flash-sale timer
 *
 * Single asset file for all prod-card.liquid behaviour:
 *   - ATC → qty-selector / view-cart transitions
 *   - Cart count badge updates
 *   - cart:change / variant:add event sync
 *   - fetch + XHR interception for /cart/add detection
 *   - ResizeObserver for --quick-buy-height CSS variable
 *   - Flash-sale countdown timer
 *
 * Feature flags (viewCartEnabled, cardQtyEnabled) are replaced by element
 * presence checks: if the element was not rendered by Liquid it simply won't
 * be in the DOM, so no data attributes are needed.
 */
(function () {
  'use strict';

  // ── Flash-sale countdown timer ────────────────────────────────────────────

  if (window.__productCardTimerId) {
    clearInterval(window.__productCardTimerId);
  }

  var DURATION_MS = 40 * 60 * 1000;
  var STORAGE_KEY = 'pct_start';

  function getMinutesLeft() {
    var start = parseInt(localStorage.getItem(STORAGE_KEY) || '0');
    var now = Date.now();
    if (!start || now - start >= DURATION_MS) {
      start = now;
      localStorage.setItem(STORAGE_KEY, String(start));
    }
    var mins = Math.floor((DURATION_MS - (now - start)) / 60000);
    return mins <= 0 ? 40 : mins;
  }

  function updateTimer() {
    var mins = getMinutesLeft();
    document.querySelectorAll('.product-card__timer-minutes').forEach(function (el) {
      el.textContent = mins;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateTimer);
  } else {
    updateTimer();
  }

  window.__productCardTimerId = setInterval(updateTimer, 60000);

  // ── Cart helpers ──────────────────────────────────────────────────────────

  function updateCartCount(count) {
    // Update every cart-count node (header bubble + any inside the drawer), not
    // just the first in document order — otherwise a removal can leave the header
    // badge stuck on its old value until reload.
    document.querySelectorAll('cart-count').forEach(function (el) {
      if (typeof el.itemCount !== 'undefined') { el.itemCount = count; return; }
      var badge = el.querySelector('[data-count]') || el.querySelector('.count') || el;
      if (badge) badge.textContent = count > 0 ? String(count) : '';
    });
    // The header wraps the bubble in .header__cart-count and hides it via an
    // opacity-0 class when the cart is empty; keep that in sync.
    document.querySelectorAll('.header__cart-count cart-count, cart-count.count-bubble').forEach(function (el) {
      el.classList.toggle('opacity-0', count === 0);
    });
  }

  // ── Session-storage cart cache (shared with prod-page.liquid) ────────────
  // Key written by both this script and prod-page.liquid so that when the user
  // navigates back/forward the PDP's latest cart state is available instantly.

  var AB_CART_KEY = 'ab_pc_cart';

  function cacheCartState(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    try {
      sessionStorage.setItem(AB_CART_KEY, JSON.stringify({
        items: cart.items.map(function (it) {
          return { product_id: it.product_id, variant_id: it.variant_id, quantity: it.quantity };
        }),
        item_count: cart.item_count || 0
      }));
    } catch (e) {}
  }

  function readCachedCart() {
    try { return JSON.parse(sessionStorage.getItem(AB_CART_KEY) || 'null'); } catch (e) { return null; }
  }

  function syncFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    cacheCartState(cart);
    var byProduct = {};
    cart.items.forEach(function (item) { byProduct[String(item.product_id)] = item; });
    document.querySelectorAll('[data-quick-buy-wrapper]').forEach(function (wrapper) {
      var item = byProduct[wrapper.getAttribute('data-quick-buy-wrapper')];
      var qtySel = wrapper.querySelector('[data-qty-selector]');
      if (item && qtySel && !isVariantProduct(wrapper)) {
        qtySel.setAttribute('data-variant-id', String(item.variant_id));
        switchToQtySelector(wrapper, item.quantity, false);
      } else if (item && !isVariantProduct(wrapper)) {
        switchToViewCart(wrapper, false);
      } else if (!item) {
        switchToAddCart(wrapper);
      }
    });
  }

  // Collect the section ids the theme needs re-rendered on a cart change (the
  // cart drawer + the header cart-count). Requesting them on /cart/change.js
  // means the response cart carries a `sections` map keyed by section id, which
  // is exactly what the theme's CartDrawer._onCartChanged consumes.
  function getCartSectionIds() {
    var ids = [];
    function add(id) { if (id && ids.indexOf(id) === -1) ids.push(id); }
    document.querySelectorAll(
      'cart-drawer, #cart-drawer, .cart-drawer, cart-notification, #cart-notification, .header__cart-count, header cart-count'
    ).forEach(function (node) {
      var sec = node.closest('[id^="shopify-section-"]');
      if (sec) add(sec.id.replace('shopify-section-', ''));
    });
    return ids;
  }

  function cartChangeQty(variantId, quantity) {
    var body = { id: variantId, quantity: quantity };
    var ids = getCartSectionIds();
    if (ids.length) body.sections = ids.join(',');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  // Apply a fresh cart to the UI. The cart drawer, header count, free-shipping
  // bar and empty-state are all owned by theme.js — we hand the cart off via the
  // theme's own `cart:change` event (the SAME contract its in-drawer qty controls
  // use), so there is a single source of truth. This is only safe because the
  // theme bugs that used to make that cascade throw are now fixed at source:
  //   - snippets/line-item.liquid     (empty <line-item-quantity> → <div>)
  //   - snippets/free-shipping-bar.liquid (added the <span>/message attrs it reads)
  //   - assets/custom.js              (guards the detail-less cart:refresh)
  // cart MUST carry `.sections` (cartChangeQty requests them) so CartDrawer can
  // re-render itself.
  function applyCartUpdate(cart) {
    if (!cart) return;
    // Optimistic, instant feedback for our own product cards + the count badge;
    // the theme then reconciles everything else from the event below.
    if (typeof cart.item_count === 'number') updateCartCount(cart.item_count);
    syncFromCart(cart);
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'line-item:change', cart: cart },
    }));
  }

  function isVariantProduct(wrapper) {
    return !!(wrapper && wrapper.querySelector('.product-card__add-btn [aria-controls]'));
  }

  function onAddSuccess(wrapper, animate, qty, variantId) {
    if (isVariantProduct(wrapper)) return;
    var qtySel = wrapper && wrapper.querySelector('[data-qty-selector]');
    if (qtySel) {
      if (variantId) qtySel.setAttribute('data-variant-id', variantId);
      switchToQtySelector(wrapper, qty || 1, animate);
    } else {
      switchToViewCart(wrapper, animate);
    }
  }

  // ── Cart drawer opener ────────────────────────────────────────────────────

  function getCartDrawerEl() {
    // Target the FULL cart drawer only. Do NOT include <cart-notification-drawer>
    // (the small "added to cart" popover that also lives in the DOM) — opening
    // that instead of the real cart was a regression.
    return document.getElementById('cart-drawer') ||
      document.querySelector('cart-drawer, mini-cart, sticky-cart, ajax-cart, side-cart');
  }

  function isCartDrawerOpen(cartEl) {
    return !!(cartEl && (cartEl.hasAttribute('open') || cartEl.classList.contains('is-open')));
  }

  // Idempotent: opens the drawer if it is closed, otherwise leaves it open.
  // It must NEVER toggle/close — toggling here is what caused the "click many
  // times to open" bug (the click both opened and immediately closed the drawer).
  function openCartDrawer() {
    var cartEl = getCartDrawerEl();
    if (cartEl) {
      if (isCartDrawerOpen(cartEl)) return;
      if (typeof cartEl.show === 'function') { cartEl.show(); return; }
      cartEl.removeAttribute('hidden');
      cartEl.removeAttribute('inert');
      cartEl.setAttribute('open', '');
      cartEl.classList.add('is-open', 'open', 'active');
      return;
    }
    // No custom element found — click the header cart icon so the theme handles state.
    var headerCartBtn = (
      document.querySelector('#cart-icon-bubble') ||
      document.querySelector('[data-cart-toggle]') ||
      document.querySelector('[data-open="cart-drawer"]') ||
      document.querySelector('button[aria-controls="CartDrawer"]') ||
      document.querySelector('button[aria-controls="cart-drawer"]') ||
      document.querySelector('button[aria-controls="cart-notification"]') ||
      document.querySelector('.header__icon--cart') ||
      document.querySelector('a[href="/cart"]:not(.product-card__view-cart-btn)')
    );
    if (headerCartBtn) { headerCartBtn.click(); return; }
    window.location.href = '/cart';
  }

  // ── State transitions ─────────────────────────────────────────────────────

  function restoreAddBtnLabel(wrapper) {
    if (!wrapper) return;
    var label = wrapper.getAttribute('data-atc-label');
    if (!label) return;
    var btn = wrapper.querySelector('.product-card__add-btn button');
    if (!btn) return;
    if (btn.getAttribute('is') === 'custom-button' && btn.firstElementChild) {
      btn.firstElementChild.textContent = label;
    } else {
      btn.textContent = label;
    }
  }

  function switchToQtySelector(wrapper, qty, animate) {
    var addBtn = wrapper.querySelector('.product-card__add-btn');
    var viewBtn = wrapper.querySelector('.product-card__view-cart-btn');
    var qtySel = wrapper.querySelector('[data-qty-selector]');
    if (!qtySel) return;
    if (addBtn) addBtn.style.display = 'none';
    if (viewBtn) {
      viewBtn.style.display = 'none';
      viewBtn.classList.remove('product-card__view-cart-btn--visible');
    }
    var countEl = qtySel.querySelector('[data-qty-count]');
    if (countEl) countEl.textContent = qty;
    qtySel.classList.remove('product-card__qty-selector--visible');
    qtySel.classList.add('is-active');
    if (animate) {
      void qtySel.offsetWidth;
      qtySel.classList.add('product-card__qty-selector--visible');
    }
  }

  function switchToViewCart(wrapper, animate) {
    var viewBtn = wrapper.querySelector('.product-card__view-cart-btn');
    if (!viewBtn) return;
    var addBtn = wrapper.querySelector('.product-card__add-btn');
    var qtySel = wrapper.querySelector('[data-qty-selector]');
    if (qtySel) qtySel.classList.remove('is-active', 'product-card__qty-selector--visible');
    if (addBtn) addBtn.style.display = 'none';
    viewBtn.style.display = 'block';
    if (animate) {
      viewBtn.classList.remove('product-card__view-cart-btn--visible');
      void viewBtn.offsetWidth;
      viewBtn.classList.add('product-card__view-cart-btn--visible');
    } else {
      viewBtn.classList.add('product-card__view-cart-btn--visible');
    }
  }

  function switchToAddCart(wrapper) {
    var addBtn = wrapper.querySelector('.product-card__add-btn');
    var viewBtn = wrapper.querySelector('.product-card__view-cart-btn');
    var qtySel = wrapper.querySelector('[data-qty-selector]');
    if (addBtn) addBtn.style.display = '';
    restoreAddBtnLabel(wrapper);
    if (viewBtn) {
      viewBtn.style.display = 'none';
      viewBtn.classList.remove('product-card__view-cart-btn--visible');
    }
    if (qtySel) {
      var countEl = qtySel.querySelector('[data-qty-count]');
      if (countEl) countEl.textContent = '1';
      qtySel.classList.remove('is-active', 'product-card__qty-selector--visible');
    }
  }

  // ── Event listeners (registered once) ────────────────────────────────────

  if (!window.__pvcSetup) {
    window.__pvcSetup = true;

    var pendingWrapper = null;
    var pendingSubmitWrapper = null;

    document.addEventListener('cart:change', function (e) {
      var cart = e.detail && e.detail.cart;
      if (!cart) return;
      if (typeof cart.item_count === 'number') updateCartCount(cart.item_count);
      syncFromCart(cart);
    });

    document.addEventListener('variant:add', function (e) {
      var detail = e.detail || {};
      var cart = detail.cart;
      var item = (detail.items || [])[0];
      if (cart && typeof cart.item_count === 'number') updateCartCount(cart.item_count);
      if (item) {
        if (pendingSubmitWrapper) {
          onAddSuccess(pendingSubmitWrapper, true, item.quantity, item.variant_id);
          pendingSubmitWrapper = null;
        } else if (pendingWrapper) {
          var w = pendingWrapper;
          pendingWrapper = null;
          onAddSuccess(w, true, item.quantity, item.variant_id);
        }
      }
      if (cart) syncFromCart(cart);
    });

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (form.getAttribute('is') !== 'product-form') return;
      pendingSubmitWrapper = form.querySelector('[data-quick-buy-wrapper]') || null;
    });

    // Cart drawer must open MANUALLY only (header cart icon / "View Cart").
    // Only the "drawer" cart type auto-opens the full <cart-drawer> on add (via
    // its `variant:add` handler, which honours `detail.blockCartDrawerOpening`).
    // We set that flag in the CAPTURE phase (runs before the theme handler) —
    // but ONLY for cartType "drawer". In "popover" mode the same event drives
    // the theme's own add-to-cart popup (CartNotificationDrawer); setting the
    // flag there would suppress that popup, so we must not. Silent cart updates
    // (count badge, qty selector switch) listen separately and are unaffected.
    if (!window.__abBlockCartAutoOpen) {
      window.__abBlockCartAutoOpen = true;
      document.addEventListener('variant:add', function (e) {
        var s = window.themeVariables && window.themeVariables.settings;
        if (s && s.cartType === 'drawer' && e.detail) e.detail.blockCartDrawerOpening = true;
      }, true);
    }

    // "View Cart" — handled in CAPTURE phase so we run BEFORE the theme's
    // focus-trap document listener. While the cart drawer is open, the
    // focus-trap treats a click on this (outside) button as an "outside click"
    // and closes the drawer — which forced the user to click again. Stopping
    // the event here prevents that, and openCartDrawer() is idempotent so it
    // only ever opens (never toggles closed).
    document.addEventListener('click', function (e) {
      var vcBtn = e.target.closest('.product-card__view-cart-btn');
      if (!vcBtn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openCartDrawer();
    }, true);

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-buy-wrapper] .product-card__add-btn [aria-controls]');
      if (btn) pendingWrapper = btn.closest('[data-quick-buy-wrapper]');

      var minusBtn = e.target.closest('.product-card__qty-minus');
      var plusBtn  = e.target.closest('.product-card__qty-plus');
      if (!minusBtn && !plusBtn) return;

      var qtySel = e.target.closest('[data-qty-selector]');
      if (!qtySel) return;

      var wrapper   = qtySel.closest('[data-quick-buy-wrapper]');
      var variantId = qtySel.getAttribute('data-variant-id');
      var countEl   = qtySel.querySelector('[data-qty-count]');
      var currentQty = parseInt(countEl && countEl.textContent, 10) || 1;

      if (minusBtn) {
        e.preventDefault();
        var nextQty = currentQty - 1;
        if (nextQty < 0) nextQty = 0;
        qtySel.classList.add('is-loading');
        cartChangeQty(variantId, nextQty)
          .then(function (cart) {
            qtySel.classList.remove('is-loading');
            // Optimistic card UI; applyCartUpdate reconciles against truth.
            if (nextQty <= 0) {
              switchToAddCart(wrapper);
            } else if (countEl) {
              countEl.textContent = nextQty;
            }
            applyCartUpdate(cart);
          })
          .catch(function () { qtySel.classList.remove('is-loading'); });
        return;
      }

      if (plusBtn) {
        e.preventDefault();
        var increasedQty = currentQty + 1;
        qtySel.classList.add('is-loading');
        cartChangeQty(variantId, increasedQty)
          .then(function (cart) {
            qtySel.classList.remove('is-loading');
            if (countEl) countEl.textContent = increasedQty;
            applyCartUpdate(cart);
          })
          .catch(function () { qtySel.classList.remove('is-loading'); });
      }
    });

    // Intercept fetch — detect /cart/add to animate the pending card on success.
    var _origFetch = window.fetch;
    window.fetch = function (resource, init) {
      var result = _origFetch.call(this, resource, init);
      var url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
      if (/\/cart\/add/.test(url)) {
        result.then(function (resp) {
          if (!resp || !resp.ok) return;
          resp.clone().json().then(function (data) {
            var item = Array.isArray(data && data.items) ? data.items[0] : data;
            if (!item || !item.variant_id) return;
            if (pendingSubmitWrapper) {
              onAddSuccess(pendingSubmitWrapper, true, item.quantity, item.variant_id);
              pendingSubmitWrapper = null;
            } else if (pendingWrapper) {
              var w = pendingWrapper;
              pendingWrapper = null;
              onAddSuccess(w, true, item.quantity, item.variant_id);
            }
          }).catch(function () {
            if (pendingSubmitWrapper) { onAddSuccess(pendingSubmitWrapper, true); pendingSubmitWrapper = null; }
            else if (pendingWrapper) { var w = pendingWrapper; pendingWrapper = null; onAddSuccess(w, true); }
          });
        }).catch(function () { pendingSubmitWrapper = null; pendingWrapper = null; });
      }
      return result;
    };

    // Intercept XHR — same purpose (Impact theme uses XHR for add-to-cart).
    var _origXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (typeof url === 'string' && /\/cart\/add/.test(url)) {
        var xhr = this;
        xhr.addEventListener('load', function () {
          if (xhr.status < 200 || xhr.status >= 300) return;
          try {
            var data = JSON.parse(xhr.responseText);
            var item = Array.isArray(data && data.items) ? data.items[0] : data;
            if (item && item.variant_id) {
              if (pendingSubmitWrapper) {
                onAddSuccess(pendingSubmitWrapper, true, item.quantity, item.variant_id);
                pendingSubmitWrapper = null;
              } else if (pendingWrapper) {
                var w = pendingWrapper;
                pendingWrapper = null;
                onAddSuccess(w, true, item.quantity, item.variant_id);
              }
            }
          } catch (err) {
            if (pendingSubmitWrapper) { onAddSuccess(pendingSubmitWrapper, true); pendingSubmitWrapper = null; }
            else if (pendingWrapper) { var w = pendingWrapper; pendingWrapper = null; onAddSuccess(w, true); }
          }
        });
      }
      return _origXHROpen.apply(this, arguments);
    };

    // ── On-load + bfcache restore sync ────────────────────────────────────
    // applyFromCacheThenFetch:
    //   1. Reads sessionStorage immediately (written by prod-page.liquid on any
    //      cart change) — zero network delay, DOM updates before first paint.
    //   2. Confirms with a live /cart.js fetch so any stale cache is corrected.
    //
    // Used on DOMContentLoaded (fixes CDN-cached stale HTML on fresh navigations)
    // and on pageshow with e.persisted (fixes bfcache back/forward restores).

    function fetchAndSync() {
      return fetch('/cart.js', { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(syncFromCart)
        .catch(function () {});
    }

    function applyFromCacheThenFetch() {
      var cached = readCachedCart();
      if (cached) syncFromCart(cached);
      fetchAndSync();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', applyFromCacheThenFetch);
    } else {
      applyFromCacheThenFetch();
    }

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) applyFromCacheThenFetch();
    });
  }

  // ── ResizeObserver for --quick-buy-height CSS variable ────────────────────

  if (!window.__pvcHeightObserver) {
    window.__pvcHeightObserver = new ResizeObserver(function (entries) {
      entries.forEach(function (entry) {
        var h = entry.contentRect.height;
        if (h <= 0) return;
        document.documentElement.style.setProperty('--quick-buy-height', h + 'px');
      });
    });
  }

  function observeAddBtns() {
    document.querySelectorAll('[data-quick-buy-wrapper] .product-card__add-btn').forEach(function (ab) {
      window.__pvcHeightObserver.observe(ab);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAddBtns);
  } else {
    observeAddBtns();
  }

  document.addEventListener('shopify:section:load', observeAddBtns);

})();
